import { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'

import useDispatch from 'hooks/useDispatch'
import { selectCurrentMerchant } from 'selectors/merchant'
import { resetForm } from 'slices/orderFeedbackReplyAddress'
import { selectIsFormDirty } from 'slices/orderFeedbackReplyAddress/selectors'

// Same shape as useFeedbackCollectionFormInitialize: key the effect on the
// merchant's IDENTITY, not the object reference, so a successful PATCH (which
// invalidates the merchant cache and produces a new object reference for the
// same merchant) does not fire again and clobber an in-progress edit.
export const useReplyAddressFormInitialize = () => {
  const dispatch = useDispatch()
  const merchant = useSelector(selectCurrentMerchant)
  const merchantId = merchant?.id

  // AI-1282: the id-keyed dep above only survives a RE-RENDER. ReplyAddressPane
  // UNMOUNTS when the merchant switches Settings sub-tabs, so on the way back
  // this effect runs again from scratch — and with no dirty guard it would
  // reset both fields to the merchant's last-persisted values, discarding an
  // edited, unpublished address with no warning. A ref, not a dependency:
  // reading the LATEST dirty flag at effect-run time is enough to guard
  // against that, without adding a dependency that would re-run this same
  // effect on every dirty <-> clean flip — in particular right after Publish,
  // before the merchant record has necessarily caught up. Same guard, for the
  // same reason, as useFeedbackCollectionFormInitialize.
  const isFormDirtyRef = useRef(false)
  isFormDirtyRef.current = useSelector(selectIsFormDirty)

  useEffect(() => {
    if (isFormDirtyRef.current) return

    if (merchant) {
      dispatch(
        resetForm({
          // `??`, not `||`: 'user' and '' are both legitimate, already-loaded
          // values, so `||` would silently coerce them back onto themselves
          // while masking a genuinely absent field the same way it masks a
          // real one — `??` only falls back when the field is null/undefined,
          // i.e. the nexus branch adding it has not deployed yet.
          replyMode: merchant.order_feedback_reply_mode ?? 'user',
          replyAddress: merchant.order_feedback_reply_address ?? '',
        })
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantId, dispatch])
}
